import type { FC, ReactNode } from 'react';
import { useEffect, useState } from 'react';

import { SWReactIcons } from '@similarweb/icons';

import { Tooltip } from '../../../../../components/Tooltip';
import type { ContactVariant, PrivateDataType } from '../../../../../domain/contact';
import { copyText } from '../../../../../services/browser';
import { translate } from '../../../../../services/i18n';

import { PrivateDataBaseItemView } from './PrivateDataBaseItemView';
import { StyledPrivateDataCopyButton } from './styles';

type Props = {
  content: ReactNode;
  type: PrivateDataType;
  variant?: ContactVariant;
  onCopyClick: (type: PrivateDataType) => void;
  animatedMount?: boolean;
  info?: ReactNode;
};

const COPY_FEEDBACK_DURATION_MS = 1000;

export const PrivateDataItemView: FC<Props> = ({ onCopyClick, content, type, variant, animatedMount, info }) => {
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!isCopied) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setIsCopied(false);
    }, COPY_FEEDBACK_DURATION_MS);

    return () => clearTimeout(timeoutId);
  }, [isCopied]);

  const handleCopyClick = () => {
    copyText(content as string)
      .then(() => {
        setIsCopied(true);
      })
      .catch((err) => console.error("Couldn't copy to clipboard", err));
    onCopyClick(type);
  };

  const tooltip = (
    <Tooltip content={translate(isCopied ? 'common.copied' : 'common.copy')} placement="left">
      <StyledPrivateDataCopyButton onClick={handleCopyClick}>
        <SWReactIcons iconName={isCopied ? 'checked' : 'copy-rounded'} size="xs" />
      </StyledPrivateDataCopyButton>
    </Tooltip>
  );

  return (
    <PrivateDataBaseItemView
      variant={variant}
      content={content}
      tooltip={tooltip}
      type={type}
      animatedMount={animatedMount}
      info={info}
    />
  );
};
